import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { cbtAPI } from '../../api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Cbt = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const navigate = useNavigate();
    
    // Main CBT form state
    const [cbtData, setCbtData] = useState({
        cbt_name: "",
        description: "",
        instructions: "",
        cbt_duration: 60,
        attempt_limit: 1,
        start_time: null,
        end_time: null,
        allow_retake_after: 0,
        max_questions: 0,
        randomize_questions: false,
        randomize_options: false,
        question_selection: "all",
        show_question_one_by_one: false,
        allow_back_navigation: true,
        allow_question_review: true,
        default_score: 1,
        passing_score: 0,
        negative_marking: false,
        negative_mark_percentage: 0,
        show_score_immediately: true,
        show_correct_answers: false,
        show_student_answers: true,
        show_explanation: false,
        allow_answer_review: true,
        require_webcam: false,
        allow_fullscreen_exit: false,
        allow_copy_paste: false,
        enable_auto_submit: true,
        max_inactivity_time: 5,
        cbt_status: 'draft',
        access_type: 'open_access',
        sections: [],
         label_id: null,
    });
    
    const [cbtList, setCbtList] = useState([]);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [buttonActive, setButtonActive] = useState(true);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const [showDuration, setShowDuration] = useState(true);
    const [showTimeSettings, setShowTimeSettings] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');   // <-- NEW

    const handleToggle = () => setIsCollapsed(!isCollapsed);

    useEffect(() => {
        fetchCbtData();
    }, []);

    const fetchCbtData = async () => {
        setLoading(true);
        try {
            const response = await cbtAPI.getMyCBTs();
            if (response.success && Array.isArray(response.data)) {
                setCbtList(response.data);
                toast.success('CBTs fetched successfully');
            } else {
                setCbtList([]);
                toast.info('No CBTs found');
            }
        } catch (error) {
            console.log(error);
            toast.error(error.data?.detail || error.message || 'Failed to fetch CBT data');
            setCbtList([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter CBTs based on search term
    const filteredCbtList = cbtList.filter(cbt =>
        cbt.cbt_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cbt.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleInputChange = (field, value) => {
        setCbtData(prev => ({ ...prev, [field]: value }));
    };

    const handleNumberChange = (field, value) => {
        const numValue = parseInt(value) || 0;
        setCbtData(prev => ({ ...prev, [field]: numValue }));
    };

    const handleBooleanChange = (field, value) => {
        setCbtData(prev => ({ ...prev, [field]: value === 'true' || value === true }));
    };

    const submitCbt = async (e) => {
        e.preventDefault();
        
        if (!cbtData.cbt_name.trim()) {
            toast.error("CBT Name cannot be empty");
            return;
        }

        const submitData = {
            cbt_name: cbtData.cbt_name,
            description: cbtData.description,
            attempt_limit: cbtData.attempt_limit,
            default_score: cbtData.default_score,
            cbt_duration: showDuration ? cbtData.cbt_duration : 0,
            max_question: cbtData.max_questions,
            cbt_status: cbtData.cbt_status,
            access_type: cbtData.access_type,
            instructions: cbtData.instructions,
        };

        setButtonActive(false);
        try {
            const response = await cbtAPI.createCBT(submitData);
            if (response.success) {
                setCbtList(prev => [...prev, response.data]);
                setCbtData({
                    cbt_name: "",
                    description: "",
                    instructions: "",
                    cbt_duration: 60,
                    attempt_limit: 1,
                    start_time: null,
                    end_time: null,
                    allow_retake_after: 0,
                    max_questions: 0,
                    randomize_questions: false,
                    randomize_options: false,
                    question_selection: "all",
                    show_question_one_by_one: false,
                    allow_back_navigation: true,
                    allow_question_review: true,
                    default_score: 1,
                    passing_score: 0,
                    negative_marking: false,
                    negative_mark_percentage: 0,
                    show_score_immediately: true,
                    show_correct_answers: false,
                    show_student_answers: true,
                    show_explanation: false,
                    allow_answer_review: true,
                    require_webcam: false,
                    allow_fullscreen_exit: false,
                    allow_copy_paste: false,
                    enable_auto_submit: true,
                    max_inactivity_time: 5,
                    cbt_status: 'draft',
                    access_type: 'open_access',
                    sections: [],
                     label_id: null
                });
                toast.success(response.data?.message || 'CBT created successfully');
                setShowConfigModal(false);
            } else {
                toast.error(response.error || 'Failed to create CBT');
            }
        } catch (error) {
            console.log(error);
            toast.error(error.data?.detail || error.message || 'Failed to create CBT');
        } finally {
            setButtonActive(true);
        }
    };

    const tabs = [
        { id: 'basic', label: 'Basic Info', icon: '📝' },
        { id: 'timing', label: 'Timing', icon: '⏰' },
        { id: 'questions', label: 'Questions', icon: '❓' },
        { id: 'scoring', label: 'Scoring', icon: '🏆' },
        { id: 'display', label: 'Display', icon: '👁️' },
        { id: 'security', label: 'Security', icon: '🔒' }
    ];

    return (
        <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
            
            <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Header Section */}
                    <div className="mb-8">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                    <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                    </div>
                                    CBT Management
                                </h1>
                                <p className="text-gray-600 mt-2">Create and manage your computer-based tests</p>
                            </div>
                            
                            <div className="flex items-center gap-3 bg-white rounded-xl shadow-sm px-4 py-3">
                                <div className="text-right">
                                    <p className="text-sm font-medium text-gray-900">Total CBTs</p>
                                    <p className="text-2xl font-bold text-blue-600">{cbtList.length}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Create CBT Card */}
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Create New CBT
                                    </h2>
                                </div>
                                
                                <div className="p-6">
                                    <form onSubmit={submitCbt}>
                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    CBT Name *
                                                </label>
                                                <input 
                                                    type="text"
                                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={cbtData.cbt_name} 
                                                    onChange={(e) => handleInputChange('cbt_name', e.target.value)}
                                                    placeholder="Enter a descriptive name for your CBT"
                                                    required
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Default Score
                                                    </label>
                                                    <input 
                                                        type="number"
                                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                        value={cbtData.default_score} 
                                                        onChange={(e) => handleNumberChange('default_score', e.target.value)}
                                                        min="1"
                                                        step="0.1"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Max Attempts
                                                    </label>
                                                    <input 
                                                        type="number"
                                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                        value={cbtData.attempt_limit} 
                                                        onChange={(e) => handleNumberChange('attempt_limit', e.target.value)}
                                                        min="1"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Description (Optional)
                                                </label>
                                                <textarea 
                                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={cbtData.description} 
                                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                                    placeholder="Describe the purpose and content of this CBT"
                                                    rows="3"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Instructions (Optional)
                                                </label>
                                                <textarea 
                                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={cbtData.instructions} 
                                                    onChange={(e) => handleInputChange('instructions', e.target.value)}
                                                    placeholder="Provide instructions for students taking this test"
                                                    rows="3"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="flex flex-col sm:flex-row gap-3 justify-between items-center mt-8 pt-6 border-t border-gray-100">
                                            <div className="text-sm text-gray-600">
                                                <p>Configure additional settings for advanced options</p>
                                            </div>
                                            
                                            <div className="flex gap-3">
                                                <button 
                                                    className="px-6 py-3 border border-blue-500 text-blue-500 rounded-xl hover:bg-blue-50 transition-all duration-200 flex items-center gap-2 font-medium"
                                                    type="button"
                                                    onClick={() => setShowConfigModal(true)}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    Advanced Settings
                                                </button>
                                                
                                                <button 
                                                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                                                    type="submit"
                                                    disabled={!buttonActive}
                                                >
                                                    {buttonActive ? (
                                                        <>
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                            </svg>
                                                            Create CBT
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Creating...
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                            
                            {/* CBT List Card */}
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-100">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                            </svg>
                                            Your CBTs ({filteredCbtList.length})
                                        </h2>

                                        {/* Search Input */}
                                        <div className="relative w-full sm:w-64">
                                            <input
                                                type="text"
                                                placeholder="Search by name or description..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            />
                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>

                                        {cbtList.length > 0 && (
                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Click on CBT name to manage questions
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="p-6">
                                    {loading ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                                <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            </div>
                                            <p className="text-gray-600 font-medium">Loading your CBTs...</p>
                                        </div>
                                    ) : filteredCbtList.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                </svg>
                                            </div>
                                            <h3 className="text-xl font-semibold text-gray-700 mb-2">
                                                {searchTerm ? 'No matching CBTs found' : 'No CBTs Created Yet'}
                                            </h3>
                                            <p className="text-gray-500 text-center mb-6 max-w-md">
                                                {searchTerm
                                                    ? `No CBT matches "${searchTerm}". Try a different keyword.`
                                                    : 'Get started by creating your first computer-based test. Configure settings and add questions to begin.'}
                                            </p>
                                            {!searchTerm && (
                                                <button 
                                                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 font-semibold hover:scale-105"
                                                    onClick={() => setShowConfigModal(true)}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    Create Your First CBT
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                            {filteredCbtList.map((data, index) => (
                                                <CbtDataCard 
                                                    key={data.cbt_data_id} 
                                                    data={data} 
                                                    index={index}
                                                    refresh={fetchCbtData}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sidebar - Quick Stats & Tips */}
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    Quick Stats
                                </h3>
                                
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Total CBTs</span>
                                        <span className="font-bold text-blue-600">{cbtList.length}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Published</span>
                                        <span className="font-bold text-green-600">
                                            {cbtList.filter(cbt => cbt.cbt_status === 'published').length}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2">
                                        <span className="text-gray-600">Drafts</span>
                                        <span className="font-bold text-yellow-600">
                                            {cbtList.filter(cbt => cbt.cbt_status === 'draft').length}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Quick Tips
                                </h3>
                                
                                <ul className="space-y-3 text-sm text-gray-600">
                                    <li className="flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Use descriptive names for easy identification
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Set appropriate time limits for your tests
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Configure access settings based on your needs
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Advanced Settings Modal - unchanged */}
            {showConfigModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-t-2xl z-10">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Advanced CBT Configuration
                                </h3>
                                <button 
                                    onClick={() => setShowConfigModal(false)}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            
                            {/* Tab Navigation */}
                            <div className="flex overflow-x-auto mt-4 pb-1">
                                {tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`px-4 py-2 rounded-lg mr-2 whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-white text-blue-600' : 'text-white hover:bg-white hover:bg-opacity-20'}`}
                                    >
                                        <span className="mr-2">{tab.icon}</span>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            {/* Basic Info Tab */}
                            {activeTab === 'basic' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">CBT Status</label>
                                        <div className="flex space-x-4">
                                            <div className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="cbt_status"
                                                    id="status_draft"
                                                    checked={cbtData.cbt_status === 'draft'}
                                                    onChange={() => handleInputChange('cbt_status', 'draft')}
                                                />
                                                <label htmlFor="status_draft" className="ml-2 text-gray-700">
                                                    Draft
                                                </label>
                                            </div>
                                            <div className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="cbt_status"
                                                    id="status_published"
                                                    checked={cbtData.cbt_status === 'published'}
                                                    onChange={() => handleInputChange('cbt_status', 'published')}
                                                />
                                                <label htmlFor="status_published" className="ml-2 text-gray-700">
                                                    Published
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">Access Type</label>
                                        <div className="flex space-x-4">
                                            <div className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="access_type"
                                                    id="open_access"
                                                    checked={cbtData.access_type === 'open_access'}
                                                    onChange={() => handleInputChange('access_type', 'open_access')}
                                                />
                                                <label htmlFor="open_access" className="ml-2 text-gray-700">
                                                    Open Access
                                                </label>
                                            </div>
                                            <div className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="access_type"
                                                    id="close_access"
                                                    checked={cbtData.access_type === 'close_access'}
                                                    onChange={() => handleInputChange('access_type', 'close_access')}
                                                />
                                                <label htmlFor="close_access" className="ml-2 text-gray-700">
                                                    Close Access
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Timing Tab */}
                            {activeTab === 'timing' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">Duration Settings</label>
                                        <div className="flex items-center space-x-4 mb-4">
                                            <div className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="duration_type"
                                                    id="duration_no"
                                                    checked={!showDuration}
                                                    onChange={() => setShowDuration(false)}
                                                />
                                                <label htmlFor="duration_no" className="ml-2 text-gray-700">
                                                    No Duration Limit
                                                </label>
                                            </div>
                                            <div className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="duration_type"
                                                    id="duration_yes"
                                                    checked={showDuration}
                                                    onChange={() => setShowDuration(true)}
                                                />
                                                <label htmlFor="duration_yes" className="ml-2 text-gray-700">
                                                    Set Duration
                                                </label>
                                            </div>
                                        </div>
                                        
                                        {showDuration && (
                                            <div className="flex items-center gap-4">
                                                <input 
                                                    type="number"
                                                    className="w-32 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={cbtData.cbt_duration}
                                                    onChange={(e) => handleNumberChange('cbt_duration', e.target.value)}
                                                    min="1"
                                                />
                                                <span className="text-gray-700">minutes</span>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">Scheduled Time (Optional)</label>
                                        <div className="flex items-center space-x-4 mb-4">
                                            <div className="flex items-center">
                                                <input 
                                                    type="checkbox"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    id="schedule_enabled"
                                                    checked={showTimeSettings}
                                                    onChange={() => setShowTimeSettings(!showTimeSettings)}
                                                />
                                                <label htmlFor="schedule_enabled" className="ml-2 text-gray-700">
                                                    Enable Scheduled Time
                                                </label>
                                            </div>
                                        </div>
                                        
                                        {showTimeSettings && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                                                    <DatePicker
                                                        selected={cbtData.start_time}
                                                        onChange={(date) => handleInputChange('start_time', date)}
                                                        showTimeSelect
                                                        dateFormat="MMMM d, yyyy h:mm aa"
                                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                        placeholderText="Select start time"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                                                    <DatePicker
                                                        selected={cbtData.end_time}
                                                        onChange={(date) => handleInputChange('end_time', date)}
                                                        showTimeSelect
                                                        dateFormat="MMMM d, yyyy h:mm aa"
                                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                        placeholderText="Select end time"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Retake After (hours)</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={cbtData.allow_retake_after}
                                            onChange={(e) => handleNumberChange('allow_retake_after', e.target.value)}
                                            min="0"
                                            placeholder="0 means no retake allowed"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Questions Tab */}
                            {activeTab === 'questions' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Maximum Questions (0 for unlimited)</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={cbtData.max_questions}
                                            onChange={(e) => handleNumberChange('max_questions', e.target.value)}
                                            min="0"
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Randomize Questions</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.randomize_questions}
                                                    onChange={(e) => handleBooleanChange('randomize_questions', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Randomize Options</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.randomize_options}
                                                    onChange={(e) => handleBooleanChange('randomize_options', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Questions One by One</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.show_question_one_by_one}
                                                    onChange={(e) => handleBooleanChange('show_question_one_by_one', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Back Navigation</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.allow_back_navigation}
                                                    onChange={(e) => handleBooleanChange('allow_back_navigation', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Question Review</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.allow_question_review}
                                                    onChange={(e) => handleBooleanChange('allow_question_review', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Scoring Tab */}
                            {activeTab === 'scoring' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Passing Score (0 for no minimum)</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={cbtData.passing_score}
                                            onChange={(e) => handleNumberChange('passing_score', e.target.value)}
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Enable Negative Marking</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.negative_marking}
                                                    onChange={(e) => handleBooleanChange('negative_marking', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        {cbtData.negative_marking && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Negative Mark Percentage (%)</label>
                                                <input 
                                                    type="number"
                                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={cbtData.negative_mark_percentage}
                                                    onChange={(e) => handleNumberChange('negative_mark_percentage', e.target.value)}
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Display Tab */}
                            {activeTab === 'display' && (
                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Score Immediately After Test</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.show_score_immediately}
                                                    onChange={(e) => handleBooleanChange('show_score_immediately', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Correct Answers</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.show_correct_answers}
                                                    onChange={(e) => handleBooleanChange('show_correct_answers', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Student Answers</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.show_student_answers}
                                                    onChange={(e) => handleBooleanChange('show_student_answers', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Explanation</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.show_explanation}
                                                    onChange={(e) => handleBooleanChange('show_explanation', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Answer Review</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.allow_answer_review}
                                                    onChange={(e) => handleBooleanChange('allow_answer_review', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Security Tab */}
                            {activeTab === 'security' && (
                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Require Webcam Monitoring</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.require_webcam}
                                                    onChange={(e) => handleBooleanChange('require_webcam', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Fullscreen Exit</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.allow_fullscreen_exit}
                                                    onChange={(e) => handleBooleanChange('allow_fullscreen_exit', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Copy/Paste</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.allow_copy_paste}
                                                    onChange={(e) => handleBooleanChange('allow_copy_paste', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Enable Auto-Submit</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={cbtData.enable_auto_submit}
                                                    onChange={(e) => handleBooleanChange('enable_auto_submit', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Max Inactivity Time (minutes)</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={cbtData.max_inactivity_time}
                                            onChange={(e) => handleNumberChange('max_inactivity_time', e.target.value)}
                                            min="1"
                                        />
                                    </div>
                                </div>
                            )}
                            
                            <div className="flex justify-between pt-6 border-t border-gray-200">
                                <button 
                                    onClick={() => setShowConfigModal(false)}
                                    className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <div className="flex gap-3">
                                    {activeTab !== 'basic' && (
                                        <button 
                                            onClick={() => {
                                                const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                                                if (currentIndex > 0) setActiveTab(tabs[currentIndex - 1].id);
                                            }}
                                            className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                                        >
                                            Previous
                                        </button>
                                    )}
                                    {activeTab !== 'security' ? (
                                        <button 
                                            onClick={() => {
                                                const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                                                if (currentIndex < tabs.length - 1) setActiveTab(tabs[currentIndex + 1].id);
                                            }}
                                            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
                                        >
                                            Next
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={() => setShowConfigModal(false)}
                                            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-colors font-medium"
                                        >
                                            Save Settings
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <ToastContainer 
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
{/* end */}
        </div>
    );
};


// ==================== CbtDataCard Component (Enhanced) ====================
const CbtDataCard = ({ data, index, refresh }) => {
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState({ ...data });
    const [loading, setLoading] = useState(false);
    
    // Edit modal additional states
    const [activeTabEdit, setActiveTabEdit] = useState('basic');
    const [showDurationEdit, setShowDurationEdit] = useState(data.cbt_duration > 0);
    const [showTimeSettingsEdit, setShowTimeSettingsEdit] = useState(
        data.start_time != null || data.end_time != null
    );

    const handleEditInputChange = (field, value) => {
        setEditData(prev => ({ ...prev, [field]: value }));
    };

    const handleEditNumberChange = (field, value) => {
        const numValue = parseInt(value) || 0;
        setEditData(prev => ({ ...prev, [field]: numValue }));
    };

    const handleEditBooleanChange = (field, value) => {
        setEditData(prev => ({ ...prev, [field]: value === 'true' || value === true }));
    };

    const deleteCbt = async () => {
        setLoading(true);
        try {
            const response = await cbtAPI.deleteCBT(data.cbt_data_id);
            if (response.success) {
                toast.success(response.data?.message || 'CBT deleted successfully');
                refresh();
            } else {
                toast.error(response.error || 'Failed to delete CBT');
            }
        } catch (error) {
            toast.error(error.data?.detail || error.message || 'Failed to delete CBT');
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
        }
    };

    const saveChanges = async () => {
        setLoading(true);
        try {
            const submitData = {
                ...editData,
                cbt_duration: showDurationEdit ? editData.cbt_duration : 0,
                start_time: showTimeSettingsEdit ? editData.start_time : null,
                end_time: showTimeSettingsEdit ? editData.end_time : null
            };
            const response = await cbtAPI.updateCBT(data.cbt_data_id, submitData);
            if (response.success) {
                toast.success(response.data?.message || 'CBT updated successfully');
                refresh();
            } else {
                toast.error(response.error || 'Failed to update CBT');
            }
        } catch (error) {
            toast.error(error.data?.detail || error.message || 'Failed to update CBT');
        } finally {
            setLoading(false);
            setShowEditModal(false);
        }
    };

    const tabs = [
        { id: 'basic', label: 'Basic Info', icon: '📝' },
        { id: 'timing', label: 'Timing', icon: '⏰' },
        { id: 'questions', label: 'Questions', icon: '❓' },
        { id: 'scoring', label: 'Scoring', icon: '🏆' },
        { id: 'display', label: 'Display', icon: '👁️' },
        { id: 'security', label: 'Security', icon: '🔒' }
    ];

    return (
        <>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 overflow-hidden">
                <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 text-lg mb-1 line-clamp-2">
                                {data.cbt_name}
                            </h3>
                            <p className="text-sm text-gray-500 mb-2">ID: {data.cbt_data_id}</p>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            data.cbt_status === 'published' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                        }`}>
                            {data.cbt_status}
                        </span>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                        <div className="flex justify-between">
                            <span>Access Type:</span>
                            <span className="font-medium">{data.access_type === 'open_access' ? 'Open' : 'Close'}</span>
                        </div>
                        {data.cbt_duration && data.cbt_duration > 0 && (
                            <div className="flex justify-between">
                                <span>Duration:</span>
                                <span className="font-medium">{data.cbt_duration} minutes</span>
                            </div>
                        )}
                        <div className="flex justify-between">
                            <span>Created:</span>
                            <span className="font-medium">{new Date(data.created_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                    
                    <div className="flex justify-between gap-2">
                        <button 
                            className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                            onClick={() => navigate(`/cbt_creator/cbt_data?cbt_data_id=${data.cbt_data_id}`)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {/* Manage */}
                        </button>
                        
                        <button 
                            className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1 text-sm"
                            onClick={() => {
                                setEditData({ ...data });
                                setShowDurationEdit(data.cbt_duration > 0);
                                setShowTimeSettingsEdit(data.start_time != null || data.end_time != null);
                                setShowEditModal(true);
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </button>
                        
                        <button 
                            className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1 text-sm"
                            onClick={() => setShowDeleteModal(true)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="bg-red-600 text-white px-6 py-4 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Confirm Deletion
                                </h3>
                                <button 
                                    onClick={() => setShowDeleteModal(false)}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                                <p className="text-red-800 font-medium">Are you sure you want to delete this CBT?</p>
                                <p className="text-red-700 text-sm mt-1">This action cannot be undone.</p>
                            </div>
                            
                            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                                <p className="mb-1"><span className="font-medium">Name:</span> {data.cbt_name}</p>
                                <p><span className="font-medium">ID:</span> {data.cbt_data_id}</p>
                            </div>
                            
                            <div className="flex justify-end space-x-3 mt-6">
                                <button 
                                    onClick={() => setShowDeleteModal(false)}
                                    className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={deleteCbt}
                                    disabled={loading}
                                    className={`px-5 py-2.5 rounded-xl text-white flex items-center gap-2 transition-colors font-medium ${
                                        loading ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                                    }`}
                                >
                                    {loading ? (
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    )}
                                    Delete Permanently
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Edit CBT Modal - Enhanced with all advanced settings */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-t-2xl z-10">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit CBT
                                </h3>
                                <button 
                                    onClick={() => setShowEditModal(false)}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            
                            {/* Tab Navigation */}
                            <div className="flex overflow-x-auto mt-4 pb-1">
                                {tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTabEdit(tab.id)}
                                        className={`px-4 py-2 rounded-lg mr-2 whitespace-nowrap transition-colors ${
                                            activeTabEdit === tab.id 
                                                ? 'bg-white text-blue-600' 
                                                : 'text-white hover:bg-white hover:bg-opacity-20'
                                        }`}
                                    >
                                        <span className="mr-2">{tab.icon}</span>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Tab Content */}
                        <div className="p-6 space-y-6">
                            {/* Basic Info Tab */}
                            {activeTabEdit === 'basic' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">CBT Name *</label>
                                        <input 
                                            type="text"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={editData.cbt_name || ''}
                                            onChange={(e) => handleEditInputChange('cbt_name', e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                                        <textarea 
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            rows="3"
                                            value={editData.description || ''}
                                            onChange={(e) => handleEditInputChange('description', e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Instructions</label>
                                        <textarea 
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            rows="3"
                                            value={editData.instructions || ''}
                                            onChange={(e) => handleEditInputChange('instructions', e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">CBT Status</label>
                                        <div className="flex space-x-4">
                                            <label className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="edit_cbt_status"
                                                    checked={editData.cbt_status === 'draft'}
                                                    onChange={() => handleEditInputChange('cbt_status', 'draft')}
                                                />
                                                <span className="ml-2 text-gray-700">Draft</span>
                                            </label>
                                            <label className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="edit_cbt_status"
                                                    checked={editData.cbt_status === 'published'}
                                                    onChange={() => handleEditInputChange('cbt_status', 'published')}
                                                />
                                                <span className="ml-2 text-gray-700">Published</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">Access Type</label>
                                        <div className="flex space-x-4">
                                            <label className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="edit_access_type"
                                                    checked={editData.access_type === 'open_access'}
                                                    onChange={() => handleEditInputChange('access_type', 'open_access')}
                                                />
                                                <span className="ml-2 text-gray-700">Open Access</span>
                                            </label>
                                            <label className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="edit_access_type"
                                                    checked={editData.access_type === 'close_access'}
                                                    onChange={() => handleEditInputChange('access_type', 'close_access')}
                                                />
                                                <span className="ml-2 text-gray-700">Close Access</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Timing Tab */}
                            {activeTabEdit === 'timing' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">Duration Settings</label>
                                        <div className="flex items-center space-x-4 mb-4">
                                            <label className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="edit_duration_type"
                                                    checked={!showDurationEdit}
                                                    onChange={() => {
                                                        setShowDurationEdit(false);
                                                        handleEditNumberChange('cbt_duration', 0);
                                                    }}
                                                />
                                                <span className="ml-2 text-gray-700">No Duration Limit</span>
                                            </label>
                                            <label className="flex items-center">
                                                <input 
                                                    type="radio"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                                    name="edit_duration_type"
                                                    checked={showDurationEdit}
                                                    onChange={() => {
                                                        setShowDurationEdit(true);
                                                        if (!editData.cbt_duration || editData.cbt_duration === 0) {
                                                            handleEditNumberChange('cbt_duration', 60);
                                                        }
                                                    }}
                                                />
                                                <span className="ml-2 text-gray-700">Set Duration</span>
                                            </label>
                                        </div>
                                        
                                        {showDurationEdit && (
                                            <div className="flex items-center gap-4">
                                                <input 
                                                    type="number"
                                                    className="w-32 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={editData.cbt_duration || 60}
                                                    onChange={(e) => handleEditNumberChange('cbt_duration', e.target.value)}
                                                    min="1"
                                                />
                                                <span className="text-gray-700">minutes</span>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">Scheduled Time (Optional)</label>
                                        <div className="flex items-center space-x-4 mb-4">
                                            <label className="flex items-center">
                                                <input 
                                                    type="checkbox"
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
                                                    checked={showTimeSettingsEdit}
                                                    onChange={() => setShowTimeSettingsEdit(!showTimeSettingsEdit)}
                                                />
                                                <span className="ml-2 text-gray-700">Enable Scheduled Time</span>
                                            </label>
                                        </div>
                                        
                                        {showTimeSettingsEdit && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                                                    <DatePicker
                                                        selected={editData.start_time ? new Date(editData.start_time) : null}
                                                        onChange={(date) => handleEditInputChange('start_time', date)}
                                                        showTimeSelect
                                                        dateFormat="MMMM d, yyyy h:mm aa"
                                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                        placeholderText="Select start time"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                                                    <DatePicker
                                                        selected={editData.end_time ? new Date(editData.end_time) : null}
                                                        onChange={(date) => handleEditInputChange('end_time', date)}
                                                        showTimeSelect
                                                        dateFormat="MMMM d, yyyy h:mm aa"
                                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                        placeholderText="Select end time"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Retake After (hours)</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={editData.allow_retake_after || 0}
                                            onChange={(e) => handleEditNumberChange('allow_retake_after', e.target.value)}
                                            min="0"
                                            placeholder="0 means no retake allowed"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Max Attempts</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={editData.attempt_limit || 1}
                                            onChange={(e) => handleEditNumberChange('attempt_limit', e.target.value)}
                                            min="1"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Questions Tab */}
                            {activeTabEdit === 'questions' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Maximum Questions (0 for unlimited)</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={editData.max_questions || 0}
                                            onChange={(e) => handleEditNumberChange('max_questions', e.target.value)}
                                            min="0"
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Randomize Questions</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.randomize_questions || false}
                                                    onChange={(e) => handleEditBooleanChange('randomize_questions', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Randomize Options</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.randomize_options || false}
                                                    onChange={(e) => handleEditBooleanChange('randomize_options', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Questions One by One</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.show_question_one_by_one || false}
                                                    onChange={(e) => handleEditBooleanChange('show_question_one_by_one', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Back Navigation</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.allow_back_navigation !== false}
                                                    onChange={(e) => handleEditBooleanChange('allow_back_navigation', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Question Review</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.allow_question_review !== false}
                                                    onChange={(e) => handleEditBooleanChange('allow_question_review', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Scoring Tab */}
                            {activeTabEdit === 'scoring' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Default Score</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={editData.default_score || 1}
                                            onChange={(e) => handleEditNumberChange('default_score', e.target.value)}
                                            min="0.1"
                                            step="0.1"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Passing Score (0 for no minimum)</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={editData.passing_score || 0}
                                            onChange={(e) => handleEditNumberChange('passing_score', e.target.value)}
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Enable Negative Marking</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.negative_marking || false}
                                                    onChange={(e) => handleEditBooleanChange('negative_marking', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        {editData.negative_marking && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Negative Mark Percentage (%)</label>
                                                <input 
                                                    type="number"
                                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                    value={editData.negative_mark_percentage || 0}
                                                    onChange={(e) => handleEditNumberChange('negative_mark_percentage', e.target.value)}
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Display Tab */}
                            {activeTabEdit === 'display' && (
                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Score Immediately After Test</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.show_score_immediately !== false}
                                                    onChange={(e) => handleEditBooleanChange('show_score_immediately', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Correct Answers</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.show_correct_answers || false}
                                                    onChange={(e) => handleEditBooleanChange('show_correct_answers', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Student Answers</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.show_student_answers !== false}
                                                    onChange={(e) => handleEditBooleanChange('show_student_answers', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Show Explanation</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.show_explanation || false}
                                                    onChange={(e) => handleEditBooleanChange('show_explanation', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Answer Review</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.allow_answer_review !== false}
                                                    onChange={(e) => handleEditBooleanChange('allow_answer_review', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Security Tab */}
                            {activeTabEdit === 'security' && (
                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Require Webcam Monitoring</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.require_webcam || false}
                                                    onChange={(e) => handleEditBooleanChange('require_webcam', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Fullscreen Exit</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.allow_fullscreen_exit || false}
                                                    onChange={(e) => handleEditBooleanChange('allow_fullscreen_exit', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Allow Copy/Paste</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.allow_copy_paste || false}
                                                    onChange={(e) => handleEditBooleanChange('allow_copy_paste', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-700">Enable Auto-Submit</span>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer"
                                                    checked={editData.enable_auto_submit !== false}
                                                    onChange={(e) => handleEditBooleanChange('enable_auto_submit', e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Max Inactivity Time (minutes)</label>
                                        <input 
                                            type="number"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            // value={editData.max_inactivity_time || 5}
                                            value={editData.max_inactivity_time}
                                            onChange={(e) => handleEditNumberChange('max_inactivity_time', e.target.value)}
                                            min="1"
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="flex justify-between pt-6 border-t border-gray-200">
                                <button 
                                    onClick={() => setShowEditModal(false)}
                                    className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <div className="flex gap-3">
                                    {activeTabEdit !== 'basic' && (
                                        <button 
                                            onClick={() => {
                                                const currentIndex = tabs.findIndex(tab => tab.id === activeTabEdit);
                                                if (currentIndex > 0) setActiveTabEdit(tabs[currentIndex - 1].id);
                                            }}
                                            className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                                        >
                                            Previous
                                        </button>
                                    )}
                                    {activeTabEdit !== 'security' ? (
                                        <button 
                                            onClick={() => {
                                                const currentIndex = tabs.findIndex(tab => tab.id === activeTabEdit);
                                                if (currentIndex < tabs.length - 1) setActiveTabEdit(tabs[currentIndex + 1].id);
                                            }}
                                            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
                                        >
                                            Next
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={saveChanges}
                                            disabled={loading}
                                            className={`px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-colors font-medium flex items-center gap-2 ${
                                                loading ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                        >
                                            {loading ? (
                                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                            Save Changes
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export { Cbt };